import { Button } from "@/components/ui/button/button";
import { BwHomeLine, BwLoadingLine } from "@/components/ui/icons";
import { BackButton } from "@/features/join/_components/back-button";
import { GeneralHeader } from "@/features/join/_components/general-header";
import { Waitlist } from "./waitlist";

type NoCoverageContentProps = {
  /** Street line of the address that fell outside coverage. */
  addressLine1: string;
  /** City, state and ZIP of that address. */
  addressLine2: string;
  /** False disables the CTA — there is no email to put on the list. */
  canJoin: boolean;
  isJoining: boolean;
  /** True swaps in the "We've got you on the list!" screen. */
  isJoined: boolean;
  onBack: () => void;
  onJoinWaitlist: () => void;
};

/**
 * The "Birdwatch is not available in your area" screen.
 *
 * Presentation only, so more than one funnel can land on the same page: the member
 * add-place step wraps it in `NoCoverage` (which supplies the cached address and the
 * signed-in email), and the presale funnel supplies its own. Keeping one copy is the
 * point — the address card, the copy and the waitlist success screen must not drift
 * between the two entry points.
 */
export function NoCoverageContent({
  addressLine1,
  addressLine2,
  canJoin,
  isJoining,
  isJoined,
  onBack,
  onJoinWaitlist,
}: NoCoverageContentProps) {
  if (isJoined) return <Waitlist />;

  return (
    <div className="flex flex-col gap-6 mt-12 px-0 sm:max-w-sm mx-auto">
      <div className="flex justify-start">
        <BackButton goBack={onBack} />
      </div>

      <div className="flex items-center gap-3 px-4 py-3 border border-dashed border-border-dark-low rounded-xl text-left">
        <BwHomeLine size={24} className="text-text-primary" />
        <div className="flex-1">
          <p className="text-sm leading-5 font-normal">{addressLine1}</p>
          <p className="text-sm leading-5 font-normal text-text-dark-high">
            {addressLine2}
          </p>
        </div>
      </div>

      <GeneralHeader
        title="Birdwatch is not available in your area"
        description="We'll let you know as soon as we are available in your area"
      />

      <Button
        type="submit"
        disabled={!canJoin || isJoining}
        onClick={onJoinWaitlist}
        size="xl"
      >
        {isJoining && <BwLoadingLine size={20} className="animate-spin" />}
        Join Waitlist
      </Button>
    </div>
  );
}
